import { Horizontal, Texto, Vertical, GraviButton } from '@gravitate-js/excalibrr'
import { Col, DatePicker, Drawer, Form, InputNumber, Row, Select, Switch } from 'antd'
import dayjs from 'dayjs'
import * as React from 'react'
import { useEffect } from 'react'
import type { CreatePointToPointDrawerProps } from '../types'
import { usePointToPointForm } from '../hooks'
import { useRateManagement } from '@api/useFreight/useFreight'
import { formatMetadataOptions } from '../utils'

/**
 * Component for creating or editing a point-to-point rate
 */
export function CreatePointToPointDrawer({
  ratebook,
  visible,
  onClose,
  onSuccess,
  editingPointToPoint,
}: CreatePointToPointDrawerProps) {
  const { useFilteredMetadataQuery } = useRateManagement()
  const { data: ratebookOptions } = useFilteredMetadataQuery(ratebook)

  const {
    form,
    showDistance,
    setShowDistance,
    hasEndDate,
    setHasEndDate,
    selectedUnit,
    submitting,
    originSelectRef,
    initializeForm,
    handleUnitChange,
    handleSubmit,
    handleClose,
  } = usePointToPointForm({
    ratebook,
    ratebookOptions,
    onSuccess,
    onClose,
  })

  // Format options for selects
  const { originOptions, destinationOptions, productGroupOptions, rateUnitOptions } =
    formatMetadataOptions(ratebookOptions)

  // Initialize form when drawer or selected item changes
  useEffect(() => {
    initializeForm(visible, editingPointToPoint)
  }, [visible, editingPointToPoint, initializeForm])

  return (
    <Drawer
      title={editingPointToPoint ? 'Edit Point to Point Rate' : 'Create Point to Point Rate'}
      placement='right'
      width={500}
      onClose={handleClose}
      open={visible}
      destroyOnClose
      styles={{
        body: {
          padding: 0,
        },
      }}
      footer={
        <Horizontal justifyContent='flex-end' gap={8}>
          <GraviButton appearance='outlined' onClick={onClose}>
            Cancel
          </GraviButton>
          <GraviButton appearance='solid' theme2 onClick={() => handleSubmit(editingPointToPoint)} loading={submitting}>
            {editingPointToPoint ? 'Update' : 'Create'}
          </GraviButton>
        </Horizontal>
      }
    >
      <Form
        form={form}
        layout='vertical'
        style={{ backgroundColor: 'var(--bg-1)', height: '100%' }}
        initialValues={{
          use_surcharge: 'Yes',
          effective_from: dayjs(),
          unit: 'gallons',
          rate: 0.0,
        }}
      >
        <Vertical className='p-3' bg='default' gap={16}>
          <Form.Item name='origin' label='Origin' rules={[{ required: true, message: 'Origin is required' }]}>
            <Select
              autoFocus
              //@ts-ignore
              ref={originSelectRef}
              placeholder='Select Origin'
              showSearch
              optionFilterProp='label'
              options={originOptions}
              style={{ width: '100%' }}
            />
          </Form.Item>

          <Form.Item
            name='destination'
            label='Destination'
            rules={[{ required: true, message: 'Destination is required' }]}
          >
            <Select
              placeholder='Select Destination'
              showSearch
              optionFilterProp='label'
              options={destinationOptions}
              style={{ width: '100%' }}
            />
          </Form.Item>

          <Form.Item name='product_group' label='Product Group'>
            <Select
              placeholder='Select Product Group'
              showSearch
              allowClear
              optionFilterProp='label'
              options={productGroupOptions}
              style={{ width: '100%' }}
            />
          </Form.Item>

          <Horizontal gap={16}>
            <Form.Item
              name='unit'
              label='Unit'
              rules={[{ required: true, message: 'Unit is required' }]}
              style={{ flex: 1 }}
            >
              <Select
                showSearch
                placeholder='Select Unit'
                options={rateUnitOptions}
                style={{ width: '100%' }}
                onChange={handleUnitChange}
              />
            </Form.Item>
            <Form.Item
              name='rate'
              label='Rate'
              rules={[
                { required: true, message: 'Please input a rate' },
                {
                  type: 'number',
                  min: 0.00001,
                  message: 'Rate must be greater than 0',
                },
              ]}
              style={{ flex: 1 }}
              extra={selectedUnit ? `Per ${selectedUnit.slice(0, -1)}` : ''}
            >
              <InputNumber
                placeholder='Enter Rate'
                min={0}
                precision={5}
                step={1.0}
                addonBefore='$'
                style={{ width: '100%' }}
              />
            </Form.Item>
          </Horizontal>

          <Horizontal gap={16}>
            <Form.Item label='Distance' style={{ flex: 1, marginBottom: 0 }}>
              <Horizontal justifyContent='space-between' verticalCenter gap={8} style={{ marginBottom: 8 }}>
                <Switch
                  checked={showDistance}
                  onChange={(checked) => {
                    setShowDistance(checked)
                    if (!checked) {
                      form.setFieldsValue({ distance: undefined })
                    }
                  }}
                />

                {showDistance ? (
                  <Form.Item name='distance' noStyle>
                    <InputNumber
                      placeholder='Enter Distance (miles)'
                      min={0}
                      precision={0}
                      addonAfter='miles'
                      style={{ width: '100%' }}
                    />
                  </Form.Item>
                ) : (
                  <div style={{ width: '100%' }} />
                )}
              </Horizontal>
            </Form.Item>

            <Form.Item name='use_surcharge' label='Use Surcharge' style={{ flex: 1 }}>
              <Select
                placeholder='Use Surcharge'
                options={[
                  { label: 'Yes', value: 'Yes' },
                  { label: 'No', value: 'No' },
                ]}
                style={{ width: '100%' }}
              />
            </Form.Item>
          </Horizontal>

          <Form.Item
            name='effective_from'
            label='Effective From'
            rules={[{ required: true, message: 'Effective From date is required' }]}
          >
            <DatePicker style={{ width: '100%' }} format='MM/DD/YYYY' />
          </Form.Item>

          <Form.Item style={{ marginBottom: 16 }}>
            <Horizontal verticalCenter gap={8}>
              <Switch checked={hasEndDate} onChange={setHasEndDate} />
              <Texto>{hasEndDate ? 'Set End Date' : 'No End Date'}</Texto>
            </Horizontal>
          </Form.Item>

          {hasEndDate && (
            <Form.Item
              name='effective_to'
              label='Effective To'
              rules={[
                { required: hasEndDate, message: 'Please select end date' },
                {
                  validator: (_, value) => {
                    const effectiveFrom = form.getFieldValue('effective_from')
                    if (value && effectiveFrom && value.isBefore(effectiveFrom)) {
                      return Promise.reject('End date must be after start date')
                    }
                    return Promise.resolve()
                  },
                },
              ]}
            >
              <DatePicker style={{ width: '100%' }} format='MM/DD/YYYY' />
            </Form.Item>
          )}
        </Vertical>
      </Form>
    </Drawer>
  )
}
